/**
 * Remembered login email.
 *
 * The sign-in screen pre-fills its Email field with the address that last
 * signed in, so staff don't retype it every launch. This matters most in
 * the Tauri desktop shell: the webview is a `tauri://` origin with no OS
 * password-manager autofill, so without this the field is always blank.
 *
 * Only the email is ever stored — never the password. The value is
 * written on a *validated* login (see LoginPage) and read on mount.
 *
 * Storage mirrors `tokenStore`: a `localStorage` key under `xop.*`, the
 * shared `isStorageAvailable` guard, and an in-memory fallback so a
 * disabled/locked-down localStorage degrades to "remembers within the
 * session" instead of throwing.
 *
 * It is deliberately NOT cleared by `tokenStore.clear()` (logout): the
 * whole point is to survive logout / relaunch. The value is only ever
 * overwritten by the next successful login.
 */
import { isStorageAvailable } from "./tokenStore.js";

const LAST_EMAIL_KEY = "xop.last_email";

let memoryLastEmail = "";

/** The last remembered login email, or "" if none was ever stored. */
export function getLastEmail(): string {
  if (!isStorageAvailable()) return memoryLastEmail;
  return window.localStorage.getItem(LAST_EMAIL_KEY) ?? "";
}

/**
 * Remember `email` as the last login address. Trims surrounding
 * whitespace; a blank value is a no-op so a cleared field never wipes a
 * good remembered value.
 */
export function setLastEmail(email: string): void {
  const trimmed = email.trim();
  if (!trimmed) return;
  if (!isStorageAvailable()) {
    memoryLastEmail = trimmed;
    return;
  }
  window.localStorage.setItem(LAST_EMAIL_KEY, trimmed);
}
